import { CSpinner } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Link, useParams } from "react-router-dom";
import { useDebounce } from "usehooks-ts";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { colors } from "../../config/theme";
import { controlStyle } from "../../helpers/constants";
import { Chip, toQuery } from "../../helpers/general";
import { useCookie } from "../../hooks/useCookie";
import { usePromoCode } from "../../hooks/usePromoCode";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { cardStyle } from "../Places/PlacesTable";
import PromoCodeList from "./PromoCodeList";

const PAGE_SIZE = 10;

type Views = "all" | "events";
interface Props {
  view?: Views;
}

const createEndpoint = (view: Views, id?: string) => {
  if (view !== "all" && !id) return "promo-codes";

  switch (view) {
    case "events":
      return `events/${id}/promo-codes`;
    default:
      return "promo-codes";
  }
};

export default function PromoCodes(props: Props) {
  const { view = "all" } = props;
  const { token } = useCookie("vToken");
  const params = useParams();
  const { toastError } = useToast();
  const { isDeleting, deletePromoCode } = usePromoCode();

  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const search = useDebounce(query, 400);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);

  const endpoint = createEndpoint(view, params?.id);

  // Search or endpoint change goes back to page 1 and clears the selection
  useEffect(() => {
    setCurrentPage(1);
  }, [search, endpoint]);

  useEffect(() => {
    setSelectedCodes([]);
  }, [currentPage, search, endpoint]);

  const { data, isLoading, isPreviousData } = useQuery({
    queryKey: ["getPromoCode", token, endpoint, currentPage, search],
    queryFn: () =>
      apiService.MakeGetRequest(
        `${endpoint}${toQuery({ page: currentPage, search })}`,
        token,
      ),
    keepPreviousData: true,
    enabled: Boolean(token),
  });

  const handleCodeSelection = (id: string, action: "add" | "remove") => {
    setSelectedCodes((prev) =>
      action === "add"
        ? prev.includes(id)
          ? prev
          : [...prev, id]
        : prev.filter((code) => code !== id),
    );
  };

  const handleMultipleDelete = async () => {
    if (selectedCodes.length === 0) return;
    try {
      await Promise.all(selectedCodes.map((code) => deletePromoCode(code)));
      setSelectedCodes([]);
    } catch (error) {
      toastError(general.fr.message.operationFailed);
    }
  };

  const total = data?.meta?.totalItems ?? 0;
  const selectedCount = selectedCodes.length;

  return (
    <section>
      {/* Title + count + add */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="fs-4 fw-bold mb-0">Codes Promo</h5>
          {data && (
            <Chip tone="warning">
              {total} {total > 1 ? "Codes" : "Code"}
            </Chip>
          )}
        </div>

        <Link
          to="/promo-codes/new"
          className="btn btn-primary text-white rounded-pill px-4"
        >
          + Ajouter un code
        </Link>
      </div>

      {/* Toolbar */}
      <div
        style={{ ...cardStyle, padding: "12px 16px" }}
        className="d-flex flex-wrap align-items-center gap-2 mb-3"
      >
        <div
          className="d-flex align-items-center gap-2 px-3 flex-grow-1"
          style={{ ...controlStyle, minWidth: 200, maxWidth: 360 }}
        >
          <MagnifyingGlassIcon color="gray" width={18} />
          <input
            type="text"
            placeholder="Rechercher un code"
            className="border-0 w-100 bg-transparent"
            style={{ outline: "none", fontSize: 13 }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <button
          type="button"
          disabled={isDeleting || selectedCount === 0}
          onClick={handleMultipleDelete}
          className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
        >
          {isDeleting ? (
            <>
              <CSpinner size="sm" /> {general.fr.wait}
            </>
          ) : (
            <>
              <DeleteIcon fill={colors.primary} /> Supprimer
              {selectedCount > 0 && ` (${selectedCount})`}
            </>
          )}
        </button>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <RenderTable
          loading={isLoading || isPreviousData}
          render={() =>
            data?.items?.length > 0 ? (
              <PromoCodeList
                // Remount on page/search change so row checkboxes reset with the selection
                key={`${currentPage}-${search}-${endpoint}`}
                codes={data.items as PromoCode[]}
                onSelect={handleCodeSelection}
              />
            ) : (
              <p className="text-center text-muted py-5 mb-0">
                {general.fr.noData}
              </p>
            )
          }
        />
      </div>

      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        siblingCount={1}
        totalCount={data?.meta?.totalPages}
        pageSize={PAGE_SIZE}
      />
    </section>
  );
}
